import { Icon } from "@/components/ui/icon";
import { TextClassContext } from "@/components/ui/text";
import { cn } from "@/lib/utils";
import * as AccordionPrimitive from "@rn-primitives/accordion";
import { Platform, Pressable, View } from "react-native";
import Animated, {
    FadeOutUp,
    LayoutAnimationConfig,
    LinearTransition,
    ReduceMotion,
    useAnimatedStyle,
    useDerivedValue,
    withTiming,
} from "react-native-reanimated";

function Accordion({
    children,
    className,
    ...props
}: Omit<React.ComponentProps<typeof AccordionPrimitive.Root>, "asChild">) {
    return (
        <LayoutAnimationConfig skipEntering>
            <AccordionPrimitive.Root
                className={className}
                {...(props as AccordionPrimitive.RootProps)}
                asChild={Platform.OS !== "web"}
            >
                <Animated.View
                    className={className}
                    layout={LinearTransition.duration(200)}
                >
                    {children}
                </Animated.View>
            </AccordionPrimitive.Root>
        </LayoutAnimationConfig>
    );
}

function AccordionItem({
    children,
    className,
    value,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
    return (
        <AccordionPrimitive.Item
            value={value}
            asChild={Platform.OS !== "web"}
            {...props}
        >
            <Animated.View
                className="native:overflow-hidden"
                layout={Platform.select({
                    native: LinearTransition.duration(200),
                })}
            >
                {children}
            </Animated.View>
        </AccordionPrimitive.Item>
    );
}

const Trigger = Platform.OS === "web" ? View : Pressable;

function AccordionTrigger({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
    children?: React.ReactNode;
}) {
    const { isExpanded } = AccordionPrimitive.useItemContext();

    const progress = useDerivedValue(
        () =>
            isExpanded
                ? withTiming(1, { duration: 250 })
                : withTiming(0, { duration: 200 }),
        [isExpanded],
    );
    const chevronStyle = useAnimatedStyle(
        () => ({
            transform: [{ rotate: `${progress.value * 180}deg` }],
        }),
        [progress],
    );

    return (
        <TextClassContext.Provider value={cn("text-left")}>
            <AccordionPrimitive.Header>
                <AccordionPrimitive.Trigger {...props} asChild>
                    <Trigger
                        className={cn(
                            "flex-row items-start justify-between gap-4 rounded-md py-4 disabled:opacity-50",
                            Platform.select({
                                web: "focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 outline-none transition-all focus-visible:ring-[3px] disabled:pointer-events-none [&[data-state=open]>svg]:rotate-180",
                            }),
                            className,
                        )}
                    >
                        <>{children}</>
                        <Animated.View
                            className="shrink-0"
                            style={chevronStyle}
                        >
                            <Icon
                                name="chevron-down"
                                size={16}
                                className={cn(
                                    "text-muted-foreground shrink-0",
                                    Platform.select({
                                        web: "pointer-events-none translate-y-0.5 transition-transform duration-200",
                                    }),
                                )}
                            />
                        </Animated.View>
                    </Trigger>
                </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
        </TextClassContext.Provider>
    );
}

function AccordionContent({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
    const { isExpanded } = AccordionPrimitive.useItemContext();
    const contentClassName = cn("pb-4", className);

    return (
        <TextClassContext.Provider value="text-sm">
            <AccordionPrimitive.Content
                className={cn(
                    "overflow-hidden",
                    Platform.select({
                        web: isExpanded
                            ? "animate-accordion-down"
                            : "animate-accordion-up",
                    }),
                )}
                {...props}
            >
                {Platform.OS === "web" ? (
                    <View className={contentClassName}>{children}</View>
                ) : (
                    <Animated.View
                        exiting={FadeOutUp.duration(200).reduceMotion(
                            ReduceMotion.System,
                        )}
                        className={contentClassName}
                    >
                        {children}
                    </Animated.View>
                )}
            </AccordionPrimitive.Content>
        </TextClassContext.Provider>
    );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
